import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useTenant } from "@/tenant/TenantProvider";
import { useOrder } from "@/store/orderStore";
import DishRow from "@/components/DishRow";
import OptionModal from "@/components/OptionModal";
import PhotoOverlay from "@/components/PhotoOverlay";
import StickyCart from "@/components/StickyCart";
import AiHint from "@/components/AiHint";
import { sumMacros } from "@/lib/format";

const BuildPlate = ({ onBack, onViewCart, onAuto }) => {
  const { state, addToCart, decrementCart } = useOrder();
  const { categories, dishes: allDishes, findDish } = useTenant();
  const { t } = useTranslation();
  const [cat, setCat] = useState(categories[0]?.id);
  const [optDish, setOptDish] = useState(null);
  const [photoDish, setPhotoDish] = useState(null);

  // Browsing never shows stop-listed dishes; findDish still resolves them.
  const dishes = useMemo(
    () => allDishes.filter((d) => d.category === cat && d.available !== false),
    [allDishes, cat]
  );
  const totals = useMemo(() => sumMacros(state.cart, findDish), [state.cart, findDish]);
  const cartCount = state.cart.reduce((s, it) => s + it.qty, 0);

  const qtyMap = useMemo(() => {
    const m = {};
    state.cart.forEach((it) => {
      m[it.dishId] = (m[it.dishId] || 0) + it.qty;
    });
    return m;
  }, [state.cart]);

  const handleAdd = (dish) => {
    if (dish.doneness || dish.style) {
      setOptDish(dish);
    } else {
      addToCart(dish.id);
    }
  };

  const handleDec = (dish) => {
    // remove the last matching cart line for this dish
    const lines = state.cart.filter((it) => it.dishId === dish.id);
    if (lines.length === 0) return;
    const last = lines[lines.length - 1];
    decrementCart(last.uid);
  };

  return (
    <div className="pb-32">
      <div className="sticky top-0 z-20 bg-[#F9F9F7]/95 backdrop-blur border-b border-neutral-100">
        <div className="px-5 pt-6 pb-3 flex items-center gap-3">
          <button
            data-testid="build-back"
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
            aria-label={t("common.back")}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
              {t("common.menu")}
            </div>
            <div className="text-base font-bold text-neutral-900 -mt-0.5">{t("build.title")}</div>
          </div>
        </div>
        <div className="overflow-x-auto no-scrollbar">
          <div className="flex gap-2 px-5 pb-3 min-w-max">
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`cat-${c.id}`}
                onClick={() => setCat(c.id)}
                className={`px-4 h-10 rounded-full text-sm font-bold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  cat === c.id
                    ? "bg-neutral-900 text-white"
                    : "bg-white text-neutral-700 border border-neutral-200"
                }`}
              >
                <span>{c.emoji}</span>
                <span>{c.short}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-5 pt-4">
        {/* The one place a guest is provably undecided: mid-menu, nothing chosen
            yet. Until this, tapping BUILD MY PLATE meant never meeting the AI
            again however long they scrolled. */}
        <AiHint onAuto={onAuto} testId="build-ai-hint" />
      </div>

      <div className="px-5 pt-5">
        <h2 className="text-2xl font-black tracking-tight text-neutral-900 mb-1">
          {categories.find((c) => c.id === cat)?.name}
        </h2>
        {/* "K IDR" is the price unit itself and stays put in every language —
            see lib/format.js. */}
        <p className="text-xs text-neutral-500 mb-4 font-mono uppercase tracking-wider">
          {t("build.countAndPrices", { count: dishes.length })}
        </p>
        <div className="bg-white border border-neutral-100 rounded-2xl px-4 shadow-sm">
          {dishes.map((d) => (
            <DishRow
              key={d.id}
              dish={d}
              qtyInCart={qtyMap[d.id] || 0}
              onAdd={handleAdd}
              onDec={handleDec}
              onOpenPhoto={setPhotoDish}
            />
          ))}
        </div>
      </div>

      <StickyCart cartCount={cartCount} totals={totals} onView={onViewCart} />

      {optDish && (
        <OptionModal
          dish={optDish}
          onClose={() => setOptDish(null)}
          onConfirm={(opts) => {
            addToCart(optDish.id, opts);
            setOptDish(null);
          }}
        />
      )}
      {photoDish && <PhotoOverlay dish={photoDish} onClose={() => setPhotoDish(null)} />}
    </div>
  );
};

export default BuildPlate;
